/**
 * 웹의 인증 상태 — 로그인 토큰 + 현재 계정 정보.
 *
 * - 토큰 자체는 localStorage 에 저장 (api-client.ts), store 는 in-memory 미러.
 * - 새로고침 시 hydrate 가 localStorage 의 토큰을 읽어 me 호출, 성공 시 account
 *   채움. 실패면 토큰 폐기 + 비로그인 상태.
 */
import { create } from "zustand";
import {
  apiClient,
  clearTokens,
  loadTokens,
  saveTokens,
  type AuthTokens,
} from "@/lib/api-client";

export interface Account {
  id: string;
  email: string;
  name: string | null;
  /** devit 시스템 관리자 여부. true 면 콘솔 사이드바에 "관리" 메뉴 노출. */
  isSuperAdmin?: boolean;
  /** 이메일 인증 완료 여부. false 면 콘솔에 안내 배너. */
  emailVerified?: boolean;
}

interface AuthState {
  status: "idle" | "loading" | "authenticated" | "unauthenticated";
  account: Account | null;
  hydrate: () => Promise<void>;
  setSession: (tokens: AuthTokens, account: Account) => void;
  signout: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  status: "idle",
  account: null,

  async hydrate(): Promise<void> {
    const tokens = loadTokens();
    if (!tokens) {
      set({ status: "unauthenticated", account: null });
      return;
    }
    set({ status: "loading" });
    try {
      const { data } = await apiClient.get<Account>("/auth/me");
      set({ status: "authenticated", account: data });
    } catch {
      clearTokens();
      set({ status: "unauthenticated", account: null });
    }
  },

  setSession(tokens, account): void {
    saveTokens(tokens);
    set({ status: "authenticated", account });
  },

  signout(): void {
    clearTokens();
    set({ status: "unauthenticated", account: null });
  },
}));
